/* eslint-disable react-hooks/exhaustive-deps */
import _ from "lodash";
import { useAtom } from "jotai";
import toast from "react-hot-toast";
import axiosInstance from "helpers/api";
import Button from "components/base/Button";
import ScrollSpy from "components/base/ScrollSpy";
import Backdrop from "components/common/Backdrop";
import { user_info } from "jotai/UserJotaiInstance";
import { Fragment, useEffect, useState } from "react";
import NoDataFound from "components/common/NoDataFound";
import CustomLoader from "components/common/CustomLoader";
import LeadProfileCard from "components/cards/LeadProfileCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faComments, faBrain, faArrowTrendUp, faArrowTrendDown, faPaperPlane, faPlus, faStickyNote, faBookmark } from "@fortawesome/free-solid-svg-icons";
import WorkflowProgressPanel from "components/cards/WorkflowProgressPanel";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import LeadDetailsOffcanvas from "components/modules/crm/LeadDetailsOffcanvas";
import LeadDetailsTab from "components/modules/crm/lead-details/LeadDetailsTab";
import ConfirmDeleteModal from "components/modules/crm/common-components/ConfirmDeleteModal";
import {
  paginationAtom,
  leadDetailsMeetingsAtom,
} from "jotai/LeadsJotaiInstance";
import { Helmet } from "react-helmet";
import './LeadDetails.css'

interface UserInfo {
  payment_status?: string;
  [key: string]: any;
}

interface ChatMessage {
  type: 'user' | 'ai';
  message: string;
  timestamp: string;
  isTyping?: boolean;
  isError?: boolean;
  context?: any;
  hasRecommendations?: boolean;
  recommendations?: string[];
}

interface LeadDetailsType {
  id: string;
  lead_first_name?: string;
  lead_last_name?: string;
  lead_email?: string;
  lead_phone?: string;
  lead_meetings: any[];
  [key: string]: any;
}

const LeadDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [notes, setNotes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [status, setStatus] = useState("idle");
  const [callNotes, setCallNotes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [meetingNotes, setMeetingNotes] = useState([]);

  const [openOffcanvas, setOpenOffcanvas] = useState(false);
  const [showLeadDetails, setShowLeadDetails] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fetchedLeadDetails, setFetchedLeadDetails] = useState(false);
  const [leadDetails, setLeadDetails] = useState<LeadDetailsType>({
    id: "",
    lead_meetings: [],
  });

  // AI Chatbot state
  const [chatbotOpen, setChatbotOpen] = useState(false);
  const [chatbotMinimized, setChatbotMinimized] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [workflowRefreshTrigger, setWorkflowRefreshTrigger] = useState(0);
  const [chatHistoryLoaded, setChatHistoryLoaded] = useState(false);

  const queryParams = new URLSearchParams(location.search);
  const type = queryParams.get("type");

  const [userInfo] = useAtom<UserInfo>(user_info);
  const [pagination, setPagination] = useAtom(paginationAtom);
  const { limit, offset, queryType } = pagination;
  const [, setLeadDetailsMeeting] = useAtom(leadDetailsMeetingsAtom);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthorized = userInfo?.payment_status === "Cancelled" ? false : true;

  const loadChatHistory = async (forceRefresh = false) => {
    if (!leadDetails?.id || (chatHistoryLoaded && !forceRefresh)) return;
    
    try {
      const response = await axiosInstance.get(`/leads/${leadDetails.id}/conversation-history`);
      
      if (response.data?.success && response.data?.data?.messages?.length > 0) {
        const { messages } = response.data.data;
        const formattedMessages: ChatMessage[] = messages.map((msg: any) => ({
          type: msg.role === 'user' ? 'user' : 'ai',
          message: msg.content,
          timestamp: msg.timestamp,
          context: msg.metadata
        }));
        
        setChatMessages(formattedMessages);
        setChatHistoryLoaded(true);
        return;
      }
    } catch (error: any) {
      console.log('Error loading chat history:', error.response?.data?.message || error.message);
    }
    
    if (leadDetails?.lead_first_name && chatMessages.length === 0) {
      const welcomeMessage: ChatMessage = {
        type: 'ai',
        message: `Hi! I'm your AI assistant for ${leadDetails.lead_first_name} ${leadDetails.lead_last_name || ''}. How can I help you today?`,
        timestamp: new Date().toISOString()
      };
      setChatMessages([welcomeMessage]);
    }
    setChatHistoryLoaded(true);
  };

  useEffect(() => {
    if (leadDetails?.id && leadDetails?.lead_first_name && !chatHistoryLoaded) {
      loadChatHistory();
    }
  }, [leadDetails?.id, leadDetails?.lead_first_name, chatHistoryLoaded]);

  const handleChatbotOpen = () => {
    setChatbotOpen(true);
    if (chatMessages.length > 0) {
      const lastMessageTime = new Date(chatMessages[chatMessages.length - 1].timestamp);
      const now = new Date();
      const minutesSinceLastMessage = (now.getTime() - lastMessageTime.getTime()) / (1000 * 60);
      
      if (minutesSinceLastMessage > 5) {
        loadChatHistory(true);
      }
    }
  };

  const handleChatSubmit = async () => {
    if (!chatInput.trim()) return;

    const userMessage: ChatMessage = {
      type: 'user',
      message: chatInput,
      timestamp: new Date().toISOString()
    };

    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');

    const typingMessage: ChatMessage = {
      type: 'ai',
      message: `Analyzing ${leadDetails.lead_first_name}'s details...`,
      timestamp: new Date().toISOString(),
      isTyping: true
    };
    setChatMessages(prev => [...prev, typingMessage]);

    try {
      const response = await axiosInstance.post('/ai-chat/lead-specific', {
        message: chatInput,
        leadId: leadDetails.id,
        userContext: {
          first_name: leadDetails.lead_first_name,
          last_name: leadDetails.lead_last_name,
          email: leadDetails.lead_email,
          phone: leadDetails.lead_phone
        }
      });

      setChatMessages(prev => prev.slice(0, -1));
      
      let aiMessage = '';
      if (response.data?.data?.message) {
        aiMessage = response.data.data.message;
      } else if (response.data?.data?.response?.message) {
        aiMessage = response.data.data.response.message;
      } else {
        aiMessage = `I understand you're asking about ${leadDetails.lead_first_name}. Here's what I can tell you...`;
      }

      const recommendations = parseRecommendations(aiMessage);
      
      const aiResponse: ChatMessage = {
        type: 'ai',
        message: aiMessage,
        timestamp: new Date().toISOString(),
        hasRecommendations: recommendations.length >= 3,
        recommendations: recommendations.length >= 3 ? recommendations : undefined
      };
      setChatMessages(prev => [...prev, aiResponse]);
    } catch (error) {
      setChatMessages(prev => prev.slice(0, -1));
      
      const errorMessage: ChatMessage = {
        type: 'ai',
        message: `I'm having trouble accessing ${leadDetails.lead_first_name}'s data right now. Please try again later.`,
        timestamp: new Date().toISOString(),
        isError: true
      };
      setChatMessages(prev => [...prev, errorMessage]);
    }
  };

  const handleSuggestion = (suggestion: string) => {
    setChatInput(suggestion);
    handleChatSubmit();
  };

  const parseRecommendations = (message: string): string[] => {
    const recommendations: string[] = [];
    const numberedMatches = message.match(/^\d+\.\s+(.+)$/gm);
    if (numberedMatches && numberedMatches.length >= 3) {
      recommendations.push(...numberedMatches.map(match => match.replace(/^\d+\.\s+/, '').trim()));
    }
    const bulletMatches = message.match(/^[-•]\s+(.+)$/gm);
    if (bulletMatches && bulletMatches.length >= 3) {
      recommendations.push(...bulletMatches.map(match => match.replace(/^[-•]\s+/, '').trim()));
    }
    return recommendations.slice(0, 10);
  };

  const handleRecommendationAction = async (action: 'tasks' | 'note' | 'save', recommendations: string[], messageIndex: number) => {
    try {
      if (action === 'tasks') {
        const tasksToCreate = recommendations.map(rec => ({
          task: rec.length > 100 ? rec.substring(0, 97) + '...' : rec,
          priority: 'medium',
          status: 'pending',
          due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
        }));

        for (const task of tasksToCreate) {
          await axiosInstance.post(`/leads/${leadDetails.id}/tasks`, task);
        }

        toast.success(`Created ${tasksToCreate.length} tasks from AI recommendations`);
        fetchTaskData();
        refreshWorkflowProgress();
      } else if (action === 'note') {
        const noteContent = `AI Recommendations (${new Date().toLocaleDateString()}):\n\n${recommendations.map((rec, i) => `${i + 1}. ${rec}`).join('\n\n')}`;
        await axiosInstance.post(`/leads/${leadDetails.id}/notes`, {
          note: noteContent,
          type: 'ai_recommendation'
        });
        toast.success('AI recommendations saved as a note');
        fetchNotes();
      } else if (action === 'save') {
        await axiosInstance.post(`/leads/${leadDetails.id}/ai-recommendations`, {
          recommendations,
          message_timestamp: chatMessages[messageIndex].timestamp,
          action_taken: 'saved'
        });
        toast.success('Recommendations saved for future reference');
      }

      setChatMessages(prev => prev.map((msg, i) => 
        i === messageIndex ? { ...msg, hasRecommendations: false } : msg
      ));
    } catch (error: any) {
      console.error('Error handling recommendation action:', error);
      toast.error('Failed to process recommendations');
    }
  };

  useEffect(() => {
    if (!isAuthorized) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === "childList") {
            const backdropExists = document.getElementById("kairos-backdrop");
            if (!backdropExists) {
              setShowLeadDetails(false);
            }
          }
        });
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
      return () => observer.disconnect();
    }
  }, [isAuthorized]);

  const handleDeleteLead = async (id: any) => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.delete(`/leads/${id}`);
      toast.success(response.data.message);
      navigate("/leads");
    } catch (error: any) {
      toast.error(error.response.data.message);
    } finally {
      setIsLoading(false);
      setShowDeleteModal(false);
    }
  };

  const fetchCallNotes = async () => {
    try {
      const response = await axiosInstance.get(`/leads/${id}/activities`, {
        params: { type: "call_note" },
      });
      setCallNotes(response.data.data.notes);
    } catch (error: any) {
      console.log(error.response.data.message);
    }
  };

  const fetchMeetingNotes = async () => {
    try {
      const response = await axiosInstance.get(`/leads/${id}/activities`, {
        params: { type: "meeting" },
      });
      setMeetingNotes(response.data.data.meetings);
    } catch (error: any) {
      console.log(error.response.data.message);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await axiosInstance.get(`/leads/${id}/activities`, {
        params: { type: "note" },
      });
      setNotes(response.data.data.notes);
    } catch (error: any) {
      console.log(error.response.data.message);
    }
  };

  const fetchTaskData = async () => {
    try {
      const response = await axiosInstance.get(`/leads/${id}/activities`, {
        params: { type: "task" },
      });
      setTasks(response.data.data.tasks);
    } catch (error: any) {
      console.log(error.response.data.message);
    }
  };

  const fetchAttachmentData = async () => {
    try {
      const response = await axiosInstance.get(`/leads/${id}/activities`, {
        params: { type: "attachment" },
      });
      setAttachments(response.data.data.attachments);
    } catch (error: any) {
      console.log(error.response.data.message);
    }
  };

  const fetchSearchActivityResults = async (searchQuery: string) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/leads/${id}/activities`, {
        params: { q: searchQuery },
      });
      setActivities(response.data.data.activities);
    } catch (err) {
      toast.error("Error fetching search results");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSearchMeetingResults = async (searchQuery: string) => {
    try {
      const response = await axiosInstance.get(`/leads/${id}/activities`, {
        params: { q: searchQuery, type: "meeting" },
      });
      setMeetingNotes(response.data.data.meetings);
    } catch (err) {
      toast.error("Error fetching search results");
    }
  };

  const fetchSearchTaskResults = async (searchQuery: string) => {
    try {
      const response = await axiosInstance.get(`/leads/${id}/activities`, {
        params: { q: searchQuery, type: "task" },
      });
      setTasks(response.data.data.tasks);
    } catch (err) {
      toast.error("Error fetching search results");
    }
  };

  useEffect(() => {
    const controller = new AbortController();

    const fetchLeadData = async () => {
      try {
        setStatus("loading");
        const response = await axiosInstance.get(`/leads/${id}`, {
          signal: controller.signal,
        });
        setLeadDetails(response.data.data.lead);
        setLeadDetailsMeeting(response.data.data.lead.lead_meetings);
        setFetchedLeadDetails(true);
      } catch (error: any) {
        const msg = error?.response?.data?.message || "Failed to fetch lead data.";
        toast.error(msg);
        setFetchedLeadDetails(false);
      } finally {
        setStatus("idle");
      }
    };

    if (id) {
      fetchLeadData();
    }

    return () => {
      controller.abort();
    };
  }, [id]);

  useEffect(() => {
    const controller = new AbortController();
    if (type === "" || type === "activity") {
      const fetchActivityData = async () => {
        try {
          setIsLoading(true);
          const response = await axiosInstance.get(`/leads/${id}/activities`, {
            params: { limit, offset, queryType },
          });
          setActivities(response.data.data.activities);
          setPagination((prev) => ({
            ...prev,
            total: response.data.data.pagination.total,
          }));
        } catch (error: any) {
          console.log(error.response.data.message);
          toast.error("Failed to load activities");
        } finally {
          setIsLoading(false);
        }
      };
      fetchActivityData();
    }

    return () => {
      controller.abort();
    };
  }, [type, limit, offset]);

  useEffect(() => {
    if (type === "call") {
      fetchCallNotes();
    }
  }, [type]);

  useEffect(() => {
    if (type === "meeting") {
      fetchMeetingNotes();
    }
  }, [type]);

  useEffect(() => {
    if (type === "notes") {
      fetchNotes();
    }
  }, [type]);

  useEffect(() => {
    if (type === "task") {
      fetchTaskData();
    }
  }, [type]);

  useEffect(() => {
    if (type === "attachments") {
      fetchAttachmentData();
    }
  }, [type]);

  const handleSearchInputChange = _.debounce((searchQuery: any) => {
    fetchSearchActivityResults(searchQuery.target.value);
  }, 500);

  const handleSearchMeeting = _.debounce((searchQuery: any) => {
    fetchSearchMeetingResults(searchQuery.target.value);
  }, 500);

  const handleSearchTask = _.debounce((searchQuery: any) => {    
    fetchSearchTaskResults(searchQuery);
  }, 500);

  const refreshWorkflowProgress = () => {
    setWorkflowRefreshTrigger(prev => prev + 1);
  };

  const refreshActivities = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get(`/leads/${id}/activities`, {
        params: { limit, offset, queryType },
      });
      setActivities(response.data.data.activities);
      setPagination((prev) => ({
        ...prev,
        total: response.data.data.pagination.total,
      }));
    } catch (error: any) {
      console.error('Failed to refresh activities:', error);
      toast.error("Failed to refresh activities timeline");
    } finally {
      setIsLoading(false);
    }
  };

  if (status === "loading") {
    return <CustomLoader />;
  }

  if (status === "idle" && Object.keys(leadDetails).length === 0) {
    return <NoDataFound type="Leads" customClass="mt-xl-15" />;
  }

  if (status === "idle" && fetchedLeadDetails) {
    return (
      <Fragment>
        <Helmet>
          <meta charSet="utf-8" />
          <title>KairosIQ.ai - {leadDetails?.lead_first_name}</title>
        </Helmet>
        {(isAuthorized || showLeadDetails) ? (
          <Fragment>
            <div className="lead-details-wrapper">
              <div className="d-flex flex-column flex-md-row gap-4 px-md-3 px-lg-5">
                {/* Sidebar - hidden on mobile */}
                <div className="d-none d-md-block" style={{ width: "360px", flexShrink: 0 }}>
                  <div className="sticky-leads-sidebar">
                    <div className="lead-details-offcanvas bg-body scrollbar">
                      <div className="d-flex justify-content-between align-items-center mb-2 d-md-none">
                        <h3 className="mb-0">Lead Details</h3>
                      </div>
                    </div>
                    <LeadProfileCard className="mb-3" leadDetails={leadDetails} />
                    <WorkflowProgressPanel 
                      className="mb-3" 
                      leadDetails={leadDetails} 
                      refreshTrigger={workflowRefreshTrigger}
                    />
                  </div>
                </div>

                {/* Mobile Header */}
                <div className="d-block d-md-none w-100 mb-3">
                  <div className="mobile-lead-header px-2">
                    <LeadProfileCard className="mb-3" leadDetails={leadDetails} />
                    <WorkflowProgressPanel 
                      className="mb-3" 
                      leadDetails={leadDetails} 
                      refreshTrigger={workflowRefreshTrigger}
                    />
                  </div>
                </div>
                
                {/* Main Content */}
                <div className="flex-grow-1 d-flex flex-column mobile-content-container">
                  <div className="lead-details-container">
                    <ScrollSpy>
                      <ScrollSpy.Content
                        id="lead-details"
                        minTopValue={150}
                        className="lead-details-scrollspy mb-8"
                      >
                        <LeadDetailsTab
                          tasks={tasks}
                          notes={notes}
                          isLoading={isLoading}
                          callNotes={callNotes}
                          activities={activities}
                          leadDetails={leadDetails}
                          meetingNotes={meetingNotes}
                          attachmentsLv={attachments}
                          handleSearchTask={handleSearchTask}
                          handleSearchMeeting={handleSearchMeeting}
                          handleSearchInputChange={handleSearchInputChange}
                          onWorkflowProgressRefresh={refreshWorkflowProgress}
                          onRefreshActivities={refreshActivities}
                        />
                      </ScrollSpy.Content>
                    </ScrollSpy>
                  </div>
                </div>
              </div>

              <LeadDetailsOffcanvas
                open={openOffcanvas}
                leadDetails={leadDetails}
                setOpen={setOpenOffcanvas}
              />

              <ConfirmDeleteModal
                type="Lead"
                modalShow={showDeleteModal}
                setModalShow={setShowDeleteModal}
                handleDelete={() => handleDeleteLead(leadDetails?.id)}
              />

              {/* Floating AI Chatbot Assistant */}
              <div className={`ai-chatbot-container ${chatbotOpen ? 'open' : ''} ${chatbotMinimized ? 'minimized' : ''}`}>
                {!chatbotOpen && (
                  <div className="chatbot-toggle" onClick={handleChatbotOpen}>
                    <div className="chatbot-toggle-content">
                      <FontAwesomeIcon icon={faComments} className="me-2" />
                      <span>AI Assistant</span>
                      <div className="ai-pulse-indicator"></div>
                    </div>
                  </div>
                )}
                
                {chatbotOpen && (
                  <div className="chatbot-panel">
                    <div className="chatbot-header">
                      <div className="d-flex align-items-center">
                        <div className="ai-avatar-small me-2">
                          <FontAwesomeIcon icon={faBrain} />
                        </div>
                        <div>
                          <h6 className="mb-0">{leadDetails.lead_first_name}'s AI Assistant</h6>
                          <small className="text-muted">Lead Analysis & Insights</small>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <Button 
                          variant="link" 
                          size="sm" 
                          className="p-0 text-muted"
                          onClick={() => setChatbotMinimized(!chatbotMinimized)}
                          title={chatbotMinimized ? "Expand" : "Minimize"}
                        >
                          <FontAwesomeIcon icon={chatbotMinimized ? faArrowTrendUp : faArrowTrendDown} />
                        </Button>
                        <Button 
                          variant="link" 
                          size="sm" 
                          className="p-0 text-muted"
                          onClick={() => setChatbotOpen(false)}
                          title="Close"
                        >
                          <FontAwesomeIcon icon={faArrowTrendUp} style={{ transform: 'rotate(45deg)' }} />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="chatbot-messages">
                      {chatMessages.map((msg, index) => (
                        <div key={index} className={`message ${msg.type}`}>
                          {msg.type === 'ai' && (
                            <div className="ai-avatar-tiny me-2">
                              <FontAwesomeIcon icon={faBrain} />
                            </div>
                          )}
                          <div className="message-content">
                            <p className="mb-1" style={{ 
                              opacity: msg.isTyping ? 0.7 : 1,
                              fontStyle: msg.isTyping ? 'italic' : 'normal'
                            }}>
                              {msg.message}
                            </p>
                            <small className="timestamp">
                              {new Date(msg.timestamp).toLocaleTimeString()}
                            </small>
                            
                            {msg.hasRecommendations && msg.recommendations && (
                              <div className="recommendation-actions mt-2">
                                <div className="d-flex gap-2 flex-wrap">
                                  <button 
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => handleRecommendationAction('tasks', msg.recommendations!, index)}
                                    title="Convert recommendations to tasks"
                                  >
                                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                                    Create Tasks ({msg.recommendations.length})
                                  </button>
                                  <button 
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => handleRecommendationAction('note', msg.recommendations!, index)}
                                    title="Save as a note"
                                  >
                                    <FontAwesomeIcon icon={faStickyNote} className="me-1" />
                                    Save as Note
                                  </button>
                                  <button 
                                    className="btn btn-sm btn-outline-info"
                                    onClick={() => handleRecommendationAction('save', msg.recommendations!, index)}
                                    title="Save for later reference"
                                  >
                                    <FontAwesomeIcon icon={faBookmark} className="me-1" />
                                    Save
                                  </button>
                                </div>
                                <small className="text-muted d-block mt-1">
                                  💡 {msg.recommendations.length} actionable recommendations detected
                                </small>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="chatbot-input">
                      <div className="d-flex">
                        <input
                          type="text"
                          className="form-control"
                          placeholder={`Ask about ${leadDetails.lead_first_name}...`}
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleChatSubmit();
                            }
                          }}
                        />
                        <Button 
                          variant="primary" 
                          className="ms-2"
                          onClick={handleChatSubmit}
                          disabled={!chatInput.trim()}
                        >
                          <FontAwesomeIcon icon={faPaperPlane} />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="chatbot-suggestions">
                      <div className="d-flex gap-2 flex-wrap">
                        <button 
                          className="suggestion-chip discovery-stage"
                          onClick={() => handleSuggestion(`What should I know about ${leadDetails.lead_first_name}?`)}
                        >
                          🔍 Lead Summary
                        </button>
                        <button 
                          className="suggestion-chip"
                          onClick={() => handleSuggestion(`What are the best next steps for ${leadDetails.lead_first_name}?`)}
                        >
                          🎯 Next Steps
                        </button>
                        <button 
                          className="suggestion-chip"
                          onClick={() => handleSuggestion("Generate a follow-up email")}
                        >
                          📧 Email Template
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Fragment>
        ) : (
          <Backdrop />
        )}
      </Fragment>
    );
  }

  return null;
};

export default LeadDetails;